import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Chip, debounce } from '@mui/material';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';

type groupData = {
  id: string;
  name: string | null;
  email?: string | null;
  type?: string | null;
}

type SelectedGroups = {
  current: groupData[];
};

type ExceptionProps = {
  selectedGroups: SelectedGroups;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function ExceptionGroups({ selectedGroups }: ExceptionProps) {

  const [data, setData] = React.useState<groupData[]>([]);
  const [selectedTags, setSelectedTags] = React.useState(() =>
    selectedGroups.current.map(group => ({ id: group.id, name: group.name }))
  );
  const [inputValue, setInputValue] = React.useState('');

  const { api } = useAppToolchain();
  const { auth } = useAuth();

  React.useEffect(() => {
    performSearch('');

    return () => {
      setData([]);
    }
  }, []);

  function convertData(data: groupData[]) {
    return data.reduce((acc, curr) => {
      if (!acc[curr.id]) {
        acc[curr.id] = curr;
      }
      return acc;
    }, {} as Record<string, groupData>)
  }

  const performSearch = React.useMemo(
    () => debounce((query: string) => {
      api.getPolicyGroups(
        auth.accountDetails.account.id,
        query
      ).then(res => {
        if (res) {
          const hash = convertData([...res.data, ...selectedGroups.current]);
          setData(Object.values(hash));
        }
      })
    }, 500),
    [api, auth.accountDetails.account.id]
  );

  const handleInputChange = (e: React.SyntheticEvent, value: string) => {
    const query = value.trimStart();
    setInputValue(query);
    performSearch(query);
  }

  const handleBlur = () => {
    if (inputValue.length > 0) {
      performSearch('');
    }
    setInputValue('');
  }

  const checkOptionSelected = (option: groupData) => {
    const obj = selectedTags.find(group => group.id === option.id);
    return obj?.id === option.id;
  }

  const filterOptions = (options: any[]) => {
    return options;
  };

  return (
    <>
      <Autocomplete
        multiple
        id="exception-groups-autocomplete"
        options={data}
        limitTags={1}
        getLimitTagsText={(tags) => <span style={{ fontSize: '12px', color: 'gray', fontWeight: 500 }}>{`+${tags} more`}</span>}
        disableCloseOnSelect
        clearOnBlur={false}
        value={selectedTags.map(group => data.find(option => option.id === group.id)).filter(option => option != null)}
        getOptionLabel={(option) => option?.name || ''}
        inputValue={inputValue}
        filterOptions={filterOptions}
        onInputChange={handleInputChange}
        onChange={(_, newValue) => {
          setInputValue(inputValue);
          setSelectedTags(newValue.map(group => ({ id: group.id, name: group.name })));
          selectedGroups.current = newValue as groupData[];
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={checkOptionSelected(option as groupData)}
                sx={{
                  color: 'black',
                  '&.Mui-checked': {
                    color: 'black',
                  },
                }}
              />
              {option?.name}
            </li>
          )
        }}
        size='small'
        renderInput={(params) => {
          return (
            <TextField {...params}
              label="Groups"
              InputLabelProps={{
                style: { fontSize: '10px', fontWeight: 'bold', lineHeight: 2 }
              }}
              onBlur={handleBlur}
            />
          )
        }}
        renderTags={(values, getTagProps) => {
          if (values[0] == null) return [];
          const renderedTags: React.ReactNode[] = [];
          values.forEach((option, index) => {
            renderedTags.push(
              <Chip
                sx={{
                  fontSize: '10px',
                  height: '20px',
                  fontWeight: 500
                }}
                size="small"
                key={option?.id}
                label={option?.name}
                {...getTagProps({ index })}
              />
            );
          });
          return renderedTags;
        }}
      />
    </>
  );
}

export default ExceptionGroups;
